import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import * as api from "../api";
import { useAuth } from "./AuthContext";

const MessagesContext = createContext();

export const MessagesProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);
  const [loading, setLoading]             = useState(false);
  const [activeConvId, setActiveConvId]   = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);

  const fetchingConvs = useRef(false);

  // ✅ Reset tout quand l'utilisateur change ou se déconnecte
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setNotifications([]);
      setUnread(0);
      setActiveConvId(null);
      setActiveMessages([]);
    }
  }, [user]);

  const fetchConversations = useCallback(async () => {
    if (!user || fetchingConvs.current) return;
    fetchingConvs.current = true;
    try {
      const res = await api.getConversations();
      // ✅ Filtrer uniquement les conversations de l'utilisateur connecté
      setConversations(res.data);
      const totalUnread = res.data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnread(totalUnread);
    } catch (err) {
      console.error("Erreur conversations:", err);
    } finally {
      fetchingConvs.current = false;
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getNotifications();
      setNotifications(res.data.notifications);
      setUnread(res.data.unread);
    } catch (err) {
      console.error("Erreur notifications:", err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchNotifications();
      const interval = setInterval(() => {
        fetchConversations();
        fetchNotifications();
      }, activeConvId ? 5000 : 15000);
      return () => clearInterval(interval);
    }
  }, [user, activeConvId, fetchConversations, fetchNotifications]);

  // Polling messages toutes les 3 secondes
  useEffect(() => {
    if (!user || !activeConvId) {
      setActiveMessages([]);
      return;
    }

    const loadMessages = async () => {
      try {
        const res = await api.getMessages(activeConvId);
        setActiveMessages(prev => {
          const serverIds = new Set(res.data.map(m => String(m.id)));
          const optimistes = prev.filter(
            m => m._optimiste && !serverIds.has(String(m.id))
          );
          return [...res.data, ...optimistes];
        });
      } catch (err) {
        console.error("Erreur getMessages:", err);
        if (err.response?.status === 403) {
          setActiveConvId(null);
          setActiveMessages([]);
        }
      }
    };

    loadMessages();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [user, activeConvId]);

  const getMessages = useCallback(async (convId) => {
    try {
      const res = await api.getMessages(convId);
      return res.data;
    } catch (err) {
      console.error("Erreur getMessages:", err);
      return [];
    }
  }, []);

  const sendMessage = async (data) => {
    const res = await api.sendMessage(data);
    // ✅ Recharger immédiatement sans délai
    fetchConversations();
    if (activeConvId) {
      try {
        const r = await api.getMessages(activeConvId);
        setActiveMessages([...r.data]);
      } catch {}
    }
    return res.data;
  };

  const marquerToutLu = async () => {
    await api.marquerToutLu();
    await fetchNotifications();
  };

  const marquerLu = async (id) => {
    await api.marquerLu(id);
    await fetchNotifications();
  };

  const [favoris, setFavoris] = useState([]);

  const fetchFavoris = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getFavoris();
      setFavoris(res.data);
    } catch (err) {
      console.error("Erreur favoris:", err);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchFavoris();
  }, [user, fetchFavoris]);

  const toggleFavori = async (annonceId) => {
    const res = await api.toggleFavori(annonceId);
    await fetchFavoris();
    return res.data;
  };

  const isFavori = (annonceId) => favoris.some((f) => f.id === annonceId);

  return (
    <MessagesContext.Provider value={{
      conversations,
      notifications,
      unread,
      loading,
      activeMessages,
      fetchConversations,
      fetchNotifications,
      getMessages,
      sendMessage,
      marquerToutLu,
      marquerLu,
      favoris,
      toggleFavori,
      isFavori,
      activeConvId,
      setActiveConvId,
    }}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => useContext(MessagesContext);
export default MessagesContext;